/**
 * Instructors Service
 * Handles all instructor-related operations.
 * Agent/partner-owned instructors use user_id; visibility controls whether others can use the profile.
 */

import { supabase } from '../lib/supabase';
import { formatSupabaseError } from './utils';

const BUCKET_INSTRUCTOR_IMAGES = 'instructor-images';

/**
 * Get all instructors for an event using RPC function
 * @param {string} eventId - The event ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getEventInstructors(eventId) {
  try {
    const { data, error } = await supabase
      .rpc('get_event_instructors', { p_event_id: eventId });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] getEventInstructors error:', error);
    return { data: null, error };
  }
}

/**
 * List instructor profiles owned by the current user (user_id = auth.uid()).
 * Used on Profile page for agents/partners.
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function listMyInstructorProfiles() {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { data: null, error: authError || { message: 'Not authenticated', code: 'auth' } };
    }
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .eq('user_id', user.id)
      .order('full_name');

    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (error) {
    console.error('[InstructorsService] listMyInstructorProfiles error:', error);
    return { data: null, error: formatSupabaseError?.(error) ?? error };
  }
}

/**
 * Create a new instructor profile owned by the current user.
 * @param {Object} data - full_name, title, bio, profile_image_url, linkedin_url, website_url, credentials, specializations, visibility, etc.
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createInstructorProfile(data) {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { data: null, error: authError || { message: 'Not authenticated', code: 'auth' } };
    }
    const payload = {
      ...data,
      user_id: user.id,
      visibility: data.visibility ?? 'private',
      is_active: true,
    };
    const { data: row, error } = await supabase
      .from('instructors')
      .insert([payload])
      .select()
      .single();

    if (error) throw error;
    return { data: row, error: null };
  } catch (error) {
    console.error('[InstructorsService] createInstructorProfile error:', error);
    return { data: null, error: formatSupabaseError?.(error) ?? error };
  }
}

/**
 * Update an instructor profile. Caller must own it (or be admin) per RLS.
 * @param {string} instructorId
 * @param {Object} updates
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateInstructorProfile(instructorId, updates) {
  return updateInstructor(instructorId, updates);
}

/**
 * Delete (hard) an instructor profile. Caller must own it or be admin.
 * @param {string} instructorId
 * @returns {Promise<{error: Error|null}>}
 */
export async function deleteInstructorProfile(instructorId) {
  return hardDeleteInstructor(instructorId);
}

/**
 * Toggle instructor visibility (private | public).
 * @param {string} instructorId
 * @param {'private' | 'public'} visibility
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function toggleInstructorVisibility(instructorId, visibility) {
  if (!['private', 'public'].includes(visibility)) {
    return { data: null, error: { message: 'Visibility must be private or public', code: 'validation' } };
  }
  return updateInstructor(instructorId, { visibility });
}

/**
 * Upload instructor profile image to Supabase Storage (instructor-images bucket).
 * Path: {userId}/{uniqueFilename}
 * @param {File} file
 * @param {string} userId - auth user id
 * @returns {Promise<{data: { path: string, fullPath?: string } | null, error: object | null }>}
 */
export async function uploadInstructorImage(file, userId) {
  try {
    if (!file || !userId) {
      return {
        data: null,
        error: { message: 'File and user ID are required.', code: 'validation' },
      };
    }
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_INSTRUCTOR_IMAGES)
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    return {
      data: {
        path: uploadData.path,
        fullPath: uploadData.fullPath ?? path,
      },
      error: null,
    };
  } catch (error) {
    console.error('[InstructorsService] uploadInstructorImage error:', error);
    return {
      data: null,
      error: formatSupabaseError?.(error) ?? error,
    };
  }
}

/**
 * Get a signed URL for an instructor image path (private bucket).
 * @param {string} path
 * @param {number} [expiresIn=3600]
 * @returns {Promise<{ data: string | null, error: object | null }>}
 */
export async function getInstructorImageUrl(path, expiresIn = 3600) {
  try {
    if (!path) return { data: null, error: null };
    const { data, error } = await supabase.storage
      .from(BUCKET_INSTRUCTOR_IMAGES)
      .createSignedUrl(path, expiresIn);
    if (error) throw error;
    return { data: data?.signedUrl ?? null, error: null };
  } catch (error) {
    console.error('[InstructorsService] getInstructorImageUrl error:', error);
    return { data: null, error: formatSupabaseError?.(error) ?? error };
  }
}

/**
 * Get all active instructors (for selection dropdown)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getAllInstructors() {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .eq('is_active', true)
      .order('full_name');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] getAllInstructors error:', error);
    return { data: null, error };
  }
}

/**
 * List instructors available to add to an event: current user's profiles + public profiles from others.
 * Excludes instructors already attached to the event when excludeEventId is provided.
 * @param {string | null} excludeEventId - Event ID to exclude already-assigned instructors
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function listAvailableInstructors(excludeEventId = null) {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { data: null, error: authError || { message: 'Not authenticated', code: 'auth' } };
    }

    const { data: instructors, error: instError } = await supabase
      .from('instructors')
      .select('*')
      .eq('is_active', true)
      .or(`user_id.eq.${user.id},visibility.eq.public`)
      .order('full_name');

    if (instError) throw instError;

    if (!excludeEventId || !instructors?.length) {
      return { data: instructors ?? [], error: null };
    }

    const { data: eventInstructors, error: eiError } = await supabase
      .from('event_instructors')
      .select('instructor_id')
      .eq('event_id', excludeEventId);

    if (eiError) throw eiError;
    const excludedIds = new Set((eventInstructors ?? []).map((ei) => ei.instructor_id));
    const filtered = (instructors ?? []).filter((i) => !excludedIds.has(i.id));
    return { data: filtered, error: null };
  } catch (error) {
    console.error('[InstructorsService] listAvailableInstructors error:', error);
    return { data: null, error: formatSupabaseError?.(error) ?? error };
  }
}

/**
 * Get a single instructor by ID
 * @param {string} instructorId - The instructor ID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function getInstructorById(instructorId) {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .select('*')
      .eq('id', instructorId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] getInstructorById error:', error);
    return { data: null, error };
  }
}

/**
 * Create new instructor
 * @param {Object} instructorData - The instructor data
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function createInstructor(instructorData) {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .insert([{
        ...instructorData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] createInstructor error:', error);
    return { data: null, error };
  }
}

/**
 * Update instructor
 * @param {string} instructorId - The instructor ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateInstructor(instructorId, updates) {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', instructorId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] updateInstructor error:', error);
    return { data: null, error };
  }
}

/**
 * Delete instructor (soft delete by setting is_active to false)
 * @param {string} instructorId - The instructor ID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function deleteInstructor(instructorId) {
  try {
    const { data, error } = await supabase
      .from('instructors')
      .update({
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', instructorId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] deleteInstructor error:', error);
    return { data: null, error };
  }
}

/**
 * Hard delete instructor
 * @param {string} instructorId - The instructor ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function hardDeleteInstructor(instructorId) {
  try {
    const { error } = await supabase
      .from('instructors')
      .delete()
      .eq('id', instructorId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[InstructorsService] hardDeleteInstructor error:', error);
    return { error };
  }
}

/**
 * Add instructor to event
 * @param {string} eventId - The event ID
 * @param {string} instructorId - The instructor ID
 * @param {string} role - The role (e.g., 'lead_instructor', 'instructor', 'guest_speaker')
 * @param {boolean} isPrimary - Whether this is the primary instructor
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function addInstructorToEvent(eventId, instructorId, role = 'instructor', isPrimary = false) {
  try {
    const { data, error } = await supabase
      .from('event_instructors')
      .insert([{
        event_id: eventId,
        instructor_id: instructorId,
        role,
        is_primary: isPrimary,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] addInstructorToEvent error:', error);
    return { data: null, error };
  }
}

/**
 * Remove instructor from event
 * @param {string} eventId - The event ID
 * @param {string} instructorId - The instructor ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function removeInstructorFromEvent(eventId, instructorId) {
  try {
    const { error } = await supabase
      .from('event_instructors')
      .delete()
      .eq('event_id', eventId)
      .eq('instructor_id', instructorId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[InstructorsService] removeInstructorFromEvent error:', error);
    return { error };
  }
}

/**
 * Update instructor role in event
 * @param {string} eventId - The event ID
 * @param {string} instructorId - The instructor ID
 * @param {Object} updates - The updates (role, is_primary, etc.)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateEventInstructor(eventId, instructorId, updates) {
  try {
    const { data, error } = await supabase
      .from('event_instructors')
      .update(updates)
      .eq('event_id', eventId)
      .eq('instructor_id', instructorId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] updateEventInstructor error:', error);
    return { data: null, error };
  }
}

/**
 * Set primary instructor for event (unsets others)
 * @param {string} eventId - The event ID
 * @param {string} instructorId - The instructor ID to set as primary
 * @returns {Promise<{error: Error|null}>}
 */
export async function setPrimaryInstructor(eventId, instructorId) {
  try {
    // First, unset all primary flags for this event
    const { error: unsetError } = await supabase
      .from('event_instructors')
      .update({ is_primary: false })
      .eq('event_id', eventId);

    if (unsetError) throw unsetError;

    // Then set the new primary
    const { error: setError } = await supabase
      .from('event_instructors')
      .update({ is_primary: true })
      .eq('event_id', eventId)
      .eq('instructor_id', instructorId);

    if (setError) throw setError;
    return { error: null };
  } catch (error) {
    console.error('[InstructorsService] setPrimaryInstructor error:', error);
    return { error };
  }
}

/**
 * Add instructor to session
 * @param {string} sessionId - The session ID
 * @param {string} instructorId - The instructor ID
 * @param {string} role - The role for this session
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function addInstructorToSession(sessionId, instructorId, role = 'instructor') {
  try {
    const { data, error } = await supabase
      .from('session_instructors')
      .insert([{
        session_id: sessionId,
        instructor_id: instructorId,
        role,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] addInstructorToSession error:', error);
    return { data: null, error };
  }
}

/**
 * Remove instructor from session
 * @param {string} sessionId - The session ID
 * @param {string} instructorId - The instructor ID
 * @returns {Promise<{error: Error|null}>}
 */
export async function removeInstructorFromSession(sessionId, instructorId) {
  try {
    const { error } = await supabase
      .from('session_instructors')
      .delete()
      .eq('session_id', sessionId)
      .eq('instructor_id', instructorId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[InstructorsService] removeInstructorFromSession error:', error);
    return { error };
  }
}

/**
 * Get session instructors with full instructor data
 * @param {string} sessionId - The session ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function getSessionInstructors(sessionId) {
  try {
    const { data, error } = await supabase
      .from('session_instructors')
      .select(`
        *,
        instructor:instructors(*)
      `)
      .eq('session_id', sessionId);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] getSessionInstructors error:', error);
    return { data: null, error };
  }
}

/**
 * Update session instructor role
 * @param {string} sessionId - The session ID
 * @param {string} instructorId - The instructor ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function updateSessionInstructor(sessionId, instructorId, updates) {
  try {
    const { data, error } = await supabase
      .from('session_instructors')
      .update(updates)
      .eq('session_id', sessionId)
      .eq('instructor_id', instructorId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[InstructorsService] updateSessionInstructor error:', error);
    return { data: null, error };
  }
}
